/**
 * TrackVision Service — Frontend API layer for athletics video analysis
 * Mirrors swimVisionService.js pattern
 */
import { AZURE } from './api';

const PREFIX = '/trackvision';

/* ─── Upload ─── */

export async function getUploadSas(filename) {
  const { data } = await AZURE.get(`${PREFIX}/upload-sas`, {
    params: { filename },
  });
  return data.data || data; // unwrap: { success, data: { sas_url, blob_name } }
}

export async function uploadVideoToBlob(sasUrl, file, onProgress) {
  const xhr = new XMLHttpRequest();
  return new Promise((resolve, reject) => {
    xhr.open('PUT', sasUrl, true);
    xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
    xhr.setRequestHeader('Content-Type', file.type || 'video/mp4');
    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      };
    }
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed: ${xhr.status}`)));
    xhr.onerror = () => reject(new Error('Upload network error'));
    xhr.send(file);
  });
}

/* ─── Sessions ─── */

export async function createSession(payload) {
  // payload: { video_blob_url, video_filename, event_type, athlete_id?, config_json? }
  const { data } = await AZURE.post(`${PREFIX}/create-session`, payload);
  return data.data || data; // unwrap: { session_id, status }
}

export async function listSessions(params = {}) {
  const { data } = await AZURE.get(`${PREFIX}/sessions`, { params });
  return data; // { success, data: [...], total, page, per_page }
}

export async function getSession(sessionId) {
  const { data } = await AZURE.get(`${PREFIX}/sessions/${sessionId}`);
  return data; // { success, data: { ... } }
}

export async function getSessionStatus(sessionId) {
  const { data } = await AZURE.get(`${PREFIX}/sessions/${sessionId}/status`);
  return data; // { success, status, progress_pct, elapsed_s, ... }
}

export async function getSessionReport(sessionId) {
  const { data } = await AZURE.get(`${PREFIX}/sessions/${sessionId}/report`);
  return data; // { success, data: { reports: [...], phases: [...], angles: [...], model_runs: [...] } }
}

export async function cancelSession(sessionId) {
  const { data } = await AZURE.post(`${PREFIX}/sessions/${sessionId}/cancel`);
  return data;
}

export async function reprocessSession(sessionId) {
  const { data } = await AZURE.post(`${PREFIX}/sessions/${sessionId}/reprocess`);
  return data;
}

/* ─── Upload + Create (convenience) ─── */

export async function uploadAndCreateSession(file, eventType = 'auto', athleteId = null, onProgress, config = {}) {
  // 1. Get SAS token
  const sas = await getUploadSas(file.name);

  // 2. Upload to blob
  await uploadVideoToBlob(sas.sas_url, file, onProgress);

  // 3. Create session
  const blobUrl = sas.sas_url.split('?')[0]; // strip SAS for storage
  const session = await createSession({
    blob_url: blobUrl,
    filename: file.name,
    event_type: eventType,
    athlete_id: athleteId,
    config: config,
  });

  return { ...session, blob_url: blobUrl };
}

/* ─── Polling ─── */

export function pollSessionStatus(sessionId, { interval = 3000, onUpdate, onComplete, onError } = {}) {
  let cancelled = false;
  const poll = async () => {
    while (!cancelled) {
      try {
        const res = await getSessionStatus(sessionId);
        // Backend returns {success, data: {status, ...}} — unwrap
        const statusData = res.data || res;
        if (onUpdate) onUpdate(statusData);
        if (statusData.status === 'completed' || statusData.status === 'failed' || statusData.status === 'cancelled') {
          if (onComplete) onComplete(statusData);
          return;
        }
      } catch (err) {
        if (onError) onError(err);
        return;
      }
      await new Promise((r) => setTimeout(r, interval));
    }
  };
  poll();
  return () => { cancelled = true; };
}
